import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { myConversations, type ConversationRow } from '@/lib/pulse/api';
import { MessageSquare, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { ConversationDetailDialog } from './ConversationDetailDialog';

export function ConversationsList({ orgId }: { orgId: string }) {
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['my_conversations', orgId],
    queryFn: async () => {
      const { data, error } = await myConversations(orgId, 50);
      if (error) throw error;
      return (data ?? []) as ConversationRow[];
    },
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!data?.length) {
    return (
      <div className="text-center py-10 text-muted-foreground">
        <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No conversations yet.</p>
      </div>
    );
  }

  return (
    <>
      <ul className="divide-y divide-border rounded-md border border-border">
        {data.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => setOpenId(c.id)}
              className="w-full flex items-start gap-3 p-3 text-left hover:bg-muted/40 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">
                  {c.first_user_turn
                    || c.summary_preview
                    || <span className="italic text-muted-foreground">(no transcript captured)</span>}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {format(new Date(c.started_at), 'MMM d, yyyy • h:mm a')}
                  {!c.ended_at && <span className="ml-2 text-amber-600">• open</span>}
                  {!c.first_user_turn && c.summary_preview && (
                    <span className="ml-2 text-muted-foreground">• summary only</span>
                  )}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground/70 mt-0.5" />
            </button>
          </li>
        ))}
      </ul>

      <ConversationDetailDialog
        conversationId={openId}
        open={!!openId}
        onOpenChange={(v) => !v && setOpenId(null)}
      />
    </>
  );
}
